// src/components/Auth/PasswordModal.js
import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contexts/AuthContext';
import { WalletContext } from '../../contexts/WalletContext';
import { getPasswordFeedback } from '../../utils/cryptoUtils';
import './PasswordModal.css';
import { FiAlertTriangle } from 'react-icons/fi';
import walletLogo from '../../assets/wallet-logo.png'; 

/**
 * Password modal for authentication and password management
 */
const PasswordModal = ({ isOpen, mode = 'login', onSuccess, onCancel }) => {
  const { t } = useTranslation();
  
  const { 
    hasPassword, 
    hasAutoPassword,
    isAuthenticated, 
    createPassword, 
    login, 
    changePassword,
    removePassword,
    authError,
    passwordDeferred,
    forceEncryptWallets,
    deferPassword,
    setupDualLayerSecurity,
    setSessionActive,
    resetWalletApp,
    autoAuthenticateWithDeferred
  } = useContext(AuthContext);
  
  const { userWallets, retryWalletDecryption } = useContext(WalletContext);
  
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [processingSuccess, setProcessingSuccess] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  
  // Reset state when modal opens or mode changes
  useEffect(() => {
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    setLoading(false);
    setProcessingSuccess(false);
    setShowResetConfirm(false);
    setResetConfirmText('');
  }, [isOpen, mode]);
  
  // Update password feedback when password changes
  useEffect(() => {
    if (mode === 'create' || mode === 'change') {
      const newPasswordToCheck = mode === 'create' ? password : newPassword;
      if (newPasswordToCheck) {
        setPasswordFeedback(getPasswordFeedback(newPasswordToCheck));
      } else {
        setPasswordFeedback(null);
      }
    }
  }, [password, newPassword, mode]);
  
  // Update error from context
  useEffect(() => {
    if (authError) {
      setError(authError);
      setLoading(false);
      setProcessingSuccess(false);
    }
  }, [authError]);
  
  // Auto-close the modal after successful operation with a small delay
  useEffect(() => {
    if (success && processingSuccess) {
      const timer = setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
        setProcessingSuccess(false);
      }, 800); // Short delay for user to see success message
      
      return () => clearTimeout(timer);
    }
  }, [success, processingSuccess, onSuccess]);
  
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    // Prevent submitting the form if already processing
    if (loading || processingSuccess) {
      return;
    }
    
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      let result = false;
      
      if (mode === 'create') {
        // Validate password strength
        if (passwordFeedback?.score < 2) {
          setError(t('auth.passwordModal.errors.weakPassword'));
          setLoading(false);
          return;
        }
        
        // Confirm password match
        if (password !== confirmPassword) {
          setError(t('auth.passwordModal.errors.passwordMismatch'));
          setLoading(false);
          return;
        }
        
        // Create new password
        result = await createPassword(password);
        if (result) {
          setSuccess(t('auth.passwordModal.success.passwordCreated'));
          
          // Mark session as active explicitly
          setSessionActive(true);
          
          // If we had an auto-generated password (deferred), mark it as not deferred now
          if (passwordDeferred) {
            deferPassword(false);
          }
          
          // Retry wallet decryption with updated auth state
          try {
            await retryWalletDecryption();
          } catch (err) {
            console.error("Failed to decrypt wallet after password creation:", err);
          }
          
          // Mark as success and will auto-close after delay
          setProcessingSuccess(true);
        }
      } else if (mode === 'login') {
        // Login with password
        result = await login(password);
        if (result) {
          setSuccess(t('auth.passwordModal.success.loggedIn'));
          
          // Mark session as active explicitly
          setSessionActive(true);
          
          // Try to decrypt wallet
          try {
            await retryWalletDecryption();
          } catch (err) {
            console.error("Failed to decrypt wallet after login:", err);
          }
          
          // Mark as success and will auto-close after delay
          setProcessingSuccess(true);
        }
      } else if (mode === 'change') {
        // Validate new password strength
        if (passwordFeedback?.score < 2) {
          setError(t('auth.passwordModal.errors.weakPassword'));
          setLoading(false);
          return;
        }
        
        // Confirm password match
        if (newPassword !== confirmPassword) {
          setError(t('auth.passwordModal.errors.passwordMismatch'));
          setLoading(false);
          return;
        }
        
        // Change password
        result = await changePassword(password, newPassword);
        if (result) {
          setSuccess(t('auth.passwordModal.success.passwordChanged'));
          
          // Mark as success and will auto-close after delay
          setProcessingSuccess(true);
        }
      } else if (mode === 'remove') {
        // Remove password - verify current password and remove
        result = await removePassword(password);
        if (result) {
          setSuccess(t('auth.passwordModal.success.passwordRemoved'));
          console.log("Password removal successful");
          
          // Update UI state to reflect changes
          try {
            await retryWalletDecryption();
          } catch (err) {
            console.error("Failed to decrypt wallet after password removal:", err);
          }
          
          // Mark as success and will auto-close after delay
          setProcessingSuccess(true);
        }
      }
    } catch (err) {
      console.error('Error in password operation:', err);
      setError(err.message || 'An error occurred');
      setProcessingSuccess(false);
    } finally {
      if (!processingSuccess) {
        setLoading(false);
      }
    }
  };
  
  // Handler for auto-authentication with deferred password
  const handleAutoAuthenticate = async () => {
    try {
      setLoading(true);
      const success = await autoAuthenticateWithDeferred();
      if (success) {
        setSuccess(t('auth.passwordModal.success.accessGranted'));
        setProcessingSuccess(true);
        
        // Try to decrypt wallet immediately
        try {
          await retryWalletDecryption();
        } catch (err) {
          console.error("Failed to decrypt wallet after auto-auth:", err);
        }
        
        // Force navigation to wallet page
        setTimeout(() => {
          // Only redirect if we're currently at the root
          if (window.location.pathname === '/') {
            window.location.href = '/wallet';
          }
        }, 500);
      } else {
        setError(t('auth.passwordModal.errors.accessFailed'));
      }
    } catch (err) {
      setError(t('auth.passwordModal.errors.accessError', { error: err.message }));
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for showing reset confirmation input
  const handleShowResetConfirm = () => {
    setShowResetConfirm(true);
    setResetConfirmText('');
  };
  
  // Handler for resetting wallet
  const handleResetWallet = () => {
    // Validate that user typed "confirm"
    if (resetConfirmText.toLowerCase() !== 'confirm') {
      return;
    }
    
    // Close modal first
    if (onCancel) onCancel();
    
    // Small delay then reset
    setTimeout(() => {
      resetWalletApp();
    }, 100);
  };
  
  // Modified to handle post-reset scenarios better
  const handlePasswordCancel = (e) => {
    if (e) e.preventDefault();
    
    if (mode === 'login') {
      // Just close without logging in - will require login again when needed
      console.log("Login canceled");
      if (onCancel) onCancel();
    } else if (mode === 'create' && (passwordDeferred || userWallets.length === 0)) {
      // Allow skipping if password is deferred OR there are no wallets (reset case)
      console.log("Password creation deferred or no wallets present");
      if (onCancel) onCancel();
      
      // If we have no wallets, force a page reload to ensure clean state
      if (userWallets.length === 0) {
        setTimeout(() => {
          window.location.replace(window.location.origin);
        }, 100);
      }
    } else {
      // For normal mandatory password creation, keep the modal open
      console.log("Cannot skip required password creation");
      
      // However, provide an emergency escape mechanism for stuck users
      // This should only be used in debug/development
      if (window.confirm(t('auth.debug.forceCloseModal'))) {
        if (onCancel) onCancel();
        setTimeout(() => {
          window.location.replace(window.location.origin);
        }, 100);
      }
    }
  };
  
  // Don't render if modal is not open
  if (!isOpen) return null;
  
  return (
    <div className="password-fullpage-overlay">
      <div className="password-fullpage">
        <div className="password-fullpage-content">
          {/* Logo and Title */}
          <div className="password-logo-container">
            <img src={walletLogo} alt={t('app.name')} className="wallet-logo" />
            <h1 className="wallet-name">
              <span className="wallet-name-first">BitDog</span>
              {" "}
              <span className="wallet-name-second">Wallet</span>
            </h1>
          </div>
          
          <div className="password-card">
            <div className="password-card-header">
              <h2>
                {mode === 'create' && t('auth.passwordModal.titles.create')}
                {mode === 'login' && t('auth.passwordModal.titles.login')}
                {mode === 'change' && t('auth.passwordModal.titles.change')}
                {mode === 'remove' && t('auth.passwordModal.titles.remove')}
              </h2>
            </div>
            
            <div className="password-card-content">
              {mode === 'create' && (
                <p className="password-info">
                  {hasAutoPassword 
                    ? t('auth.passwordModal.info.createWithAutoSecurity')
                    : t('auth.passwordModal.info.createStandard')
                  }
                </p>
              )}
              
              {mode === 'login' && hasPassword && !showResetConfirm && (
                <p className="password-info">
                  {t('auth.passwordModal.info.login')}
                </p>
              )}
              
              {mode === 'change' && (
                <p className="password-info">
                  {t('auth.passwordModal.info.change')}
                </p>
              )}
              
              {mode === 'remove' && (
                <p className="password-info">
                  {t('auth.passwordModal.info.removeWarning')}
                </p>
              )}
              
              {!showResetConfirm ? (
                <form onSubmit={handleSubmit}>
                  {/* Current password field */}
                  {(mode === 'login' || mode === 'change' || mode === 'remove') && !showResetConfirm && (
                    <div className="password-input-group">
                      <label htmlFor="current-password">
                        {mode === 'login' 
                          ? t('auth.passwordModal.labels.password') 
                          : t('auth.passwordModal.labels.currentPassword')
                        }
                      </label>
                      <input
                        id="current-password"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder={t('auth.passwordModal.placeholders.enterPassword')}
                        autoComplete={mode === 'login' ? 'current-password' : 'off'}
                      />
                    </div>
                  )}
                  
                  {/* New password fields for create/change modes */}
                  {(mode === 'create' || mode === 'change') && (
                    <>
                      <div className="password-input-group">
                        <label htmlFor="new-password">
                          {mode === 'create' 
                            ? t('auth.passwordModal.labels.password') 
                            : t('auth.passwordModal.labels.newPassword')
                          }
                        </label>
                        <input
                          id="new-password"
                          type="password"
                          placeholder={t('auth.passwordModal.placeholders.enterPassword')}
                          value={mode === 'create' ? password : newPassword}
                          onChange={(e) => {
                            if (mode === 'create') {
                              setPassword(e.target.value);
                            } else {
                              setNewPassword(e.target.value);
                            }
                          }}
                          required
                          autoComplete={mode === 'create' ? 'new-password' : 'off'}
                          minLength={8}
                        />
                        
                        {/* Password strength indicator */}
                        {passwordFeedback && (
                          <div className="password-strength">
                            <div 
                              className="strength-meter" 
                              style={{ 
                                width: `${(passwordFeedback.score + 1) * 20}%`,
                                backgroundColor: passwordFeedback.color
                              }} 
                            />
                            <span className="strength-text">{passwordFeedback.feedback}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="password-input-group">
                        <label htmlFor="confirm-password">{t('auth.passwordModal.labels.confirmPassword')}</label>
                        <input
                          id="confirm-password"
                          type="password"
                          placeholder={t('auth.passwordModal.placeholders.confirmPassword')}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete={mode === 'create' ? 'new-password' : 'off'}
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Error and success messages */}
                  {error && <div className="password-error">{error}</div>}
                  {success && <div className="password-success">{success}</div>}
                  
                  {/* Special option for users who deferred password */}
                  {mode === 'login' && passwordDeferred && (
                    <div className="password-deferred-notice">
                      <p>{t('auth.passwordModal.info.deferredNotice')}</p>
                      <div className="deferred-options">
                        <button 
                          type="button" 
                          className="deferred-option-btn"
                          onClick={handleAutoAuthenticate}
                          disabled={loading}
                        >
                          {loading 
                            ? t('auth.passwordModal.buttons.accessing') 
                            : t('auth.passwordModal.buttons.accessAutoSecurity')
                          }
                        </button>
                        <span className="option-separator">or</span>
                        <button 
                          type="button" 
                          className="forgot-password-link"
                          onClick={handleShowResetConfirm}
                          disabled={loading}
                        >
                          {t('auth.passwordModal.buttons.resetWallet')}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Reset Wallet option for login mode */}
                  {mode === 'login' && !passwordDeferred && !showResetConfirm && (
                    <div className="forgot-password-container">
                      <button 
                        type="button" 
                        className="forgot-password-link"
                        onClick={handleShowResetConfirm}
                        disabled={loading}
                      >
                        {t('auth.passwordModal.buttons.forgotPassword')}
                      </button>
                    </div>
                  )}
                  
                  {/* Submit button */}
                  <div className="password-modal-actions">
                    {/* Only show Cancel button in certain contexts */}
                    {(mode !== 'login' || passwordDeferred || userWallets.length === 0) && (
                      <button 
                        type="button" 
                        className="cancel-button" 
                        onClick={handlePasswordCancel}
                        disabled={loading || processingSuccess}
                      >
                        {t('auth.passwordModal.buttons.cancel')}
                      </button>
                    )}
                    <button 
                      type="submit" 
                      className="submit-button"
                      disabled={loading || processingSuccess}
                    >
                      {loading || processingSuccess ? (
                        <span className=""></span>
                      ) : (
                        <>
                          {mode === 'create' && t('auth.passwordModal.buttons.create')}
                          {mode === 'login' && t('auth.passwordModal.buttons.unlock')}
                          {mode === 'change' && t('auth.passwordModal.buttons.update')}
                          {mode === 'remove' && t('auth.passwordModal.buttons.remove')}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                /* Reset Wallet Confirmation UI */
                <div className="reset-wallet-confirm">
                  <div className="reset-warning">
                    <FiAlertTriangle className="reset-warning-icon" />
                    <h3>{t('auth.passwordModal.reset.confirmTitle')}</h3>
                    <p>{t('auth.passwordModal.reset.warning')}</p>
                  </div>
                  
                  <div className="reset-confirm-input-group">
                    <label htmlFor="reset-confirm">{t('auth.passwordModal.reset.typeConfirm')}</label>
                    <input
                      id="reset-confirm"
                      type="text"
                      value={resetConfirmText}
                      onChange={(e) => setResetConfirmText(e.target.value)}
                      placeholder={t('auth.passwordModal.placeholders.confirmReset')}
                      className="reset-confirm-input"
                      autoFocus
                    />
                  </div>
                  
                  <div className="password-modal-actions">
                    <button 
                      type="button" 
                      className="cancel-button" 
                      onClick={() => setShowResetConfirm(false)}
                    >
                      {t('auth.passwordModal.buttons.cancel')}
                    </button>
                    <button 
                      type="button" 
                      className="danger-button"
                      onClick={handleResetWallet}
                      disabled={resetConfirmText.toLowerCase() !== 'confirm'}
                    >
                      {t('auth.passwordModal.buttons.resetWallet')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordModal;